import { create } from 'zustand';
import { filterByLangValue, sortByCost, sortByLangValue } from '../features/table/helpers';
import { PaginationType } from '../types/pagination';
import { SortType } from '../types/props';
import {
  deleteTree,
  fetchTree,
  fetchTreeList,
  FetchTreeListRequest,
  postTree,
  putTree,
  restoreTree,
  TreeNodeType,
  TreeType,
} from './api/trees-api';
import { useAlertStore } from './use-alert-store';

interface Store extends Partial<PaginationType> {
  postTree: () => Promise<void>;
  putTree: () => Promise<void>;
  tree?: Partial<TreeType>;
  node?: Partial<TreeNodeType>;
  isLoadingFetch?: boolean;
  isLoading?: boolean;
  list?: TreeType[];
  fetchTreeList: (params: FetchTreeListRequest) => Promise<void>;
  fetchTree: (id: string) => Promise<void>;
  handleUpdateTree: (data: Partial<TreeType>) => void;
  handleResetTree: () => void;
  handleResetNode: () => void;
  handleUpdateNode: (data?: Partial<TreeNodeType>) => void;
  deleteTree: (id: string) => Promise<void>;
  restoreTree: (id: string) => Promise<void>;
}

export const useTreeStore = create<Store>((set, get) => ({
  handleUpdateTree: (data) => {
    set((store) => ({
      ...store,
      tree: {
        ...store.tree,
        ...data,
      },
    }));
  },
  handleUpdateNode: (data) => {
    set((store) => ({
      ...store,
      node: {
        ...store.node,
        ...data,
      },
    }));
  },
  handleResetNode: () => {
    set((store) => ({
      ...store,
      node: undefined,
    }));
  },
  handleResetTree: () => {
    set((store) => ({
      ...store,
      tree: undefined,
    }));
  },
  postTree: async () => {
    set({ isLoading: true });
    try {
      const data = get().tree;
      if (!data) {
        throw new Error('Data is not defined');
      }

      await postTree(data);
      set({ tree: undefined });
      useAlertStore.getState().add({ label: 'Дерево успешно создано', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по дереву', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  putTree: async () => {
    set({ isLoading: true });
    try {
      const data = get().tree;
      if (!data) {
        throw new Error('Data is not defined');
      }
      await putTree(data);
      set({ tree: undefined });
      useAlertStore.getState().add({ label: 'Дерево успешно изменено', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по дереву', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  fetchTreeList: async (params) => {
    set({ isLoadingFetch: true });
    try {
      const { trees, ...other } = await fetchTreeList(params);
      let list = [...trees];

      if (params.name) {
        list = list.filter((item) => filterByLangValue(item.name, params.name));
      }

      if (params.description) {
        list = list.filter((item) => filterByLangValue(item.description, params.description));
      }

      if (params.qr) {
        list = list.filter((item) => {
          const filteredQrs = item.qr?.filter((qr) => filterByLangValue(qr.value, params.qr));
          return filteredQrs && filteredQrs.length > 0;
        });
      }

      if (params.branch) {
        list = list.filter((item) => item.branches?.some((branch) => String(branch.id) === params.branch));
      }

      if (params.field == 'name') {
        list = list.sort((a, b) => sortByLangValue(a.name, b.name, params.dir as SortType));
      }

      if (params.field == 'qr') {
        list = list.sort((a, b) => sortByCost(a.qr?.length, b.qr?.length, params.dir as SortType));
      }

      if(params.field === 'branch') {
        list = list.sort((a, b) => sortByCost(a.branches?.length, b.branches?.length, params.dir as SortType));
      }


      set({ list, ...other });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по деревьям', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  fetchTree: async (id) => {
    set({ isLoadingFetch: true });
    try {
      const resp = await fetchTree(id);
      set({ tree: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по дереву', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  deleteTree: async (id) => {
    set({ isLoading: true });
    try {
      await deleteTree(id);
      useAlertStore.getState().add({ label: 'Дерево успешно удалено', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: true } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить дерево', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreTree: async (id) => {
    set({ isLoading: true });
    try {
      await restoreTree(id);
      useAlertStore.getState().add({ label: 'Дерево успешно восстановлено', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: false } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось восстановить дерево', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
